// Display · StatBand (composite) + Stat (sibling cell)
//
// KPI band — horizontal grid of cells. StatBand owns the outer grid;
// Stat is a leaf cell with bespoke padding + right-edge separator + the
// editorial typography stack.
//
// All text flows through TextValue; the cell wrapper, progress bar chrome,
// and asymmetric padding remain raw HTML because they're structural leaf
// chrome that the closed-enum Box doesn't model.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  SKELETON
} from "@ds/tokens/palette";
import { Skeleton as SkeletonBlock } from "@ds/display/Skeleton";
import { TextValue, type TextTone } from "@ds/display/TextValue";
import { ProgressBar } from "@ds/display/ProgressBar";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { Grid } from "@ds/layout/Grid";
import { type StatTone } from "@ds/display/StatBand.helpers";

export type { StatTone } from "@ds/display/StatBand.helpers";

// ─── Tone mapping ─────────────────────────────────────────────────────────

const VALUE_TONE: Record<StatTone, TextTone> = {
  default:  "primary",
  positive: "revenue",
  warning:  "amber",
  negative: "cost",
};

const SUB_TONE: Record<StatTone, TextTone> = {
  default:  "muted",
  positive: "revenue",
  warning:  "amber",
  negative: "cost",
};

function slugify(label: ReactNode, fallback: number): string {
  if (typeof label !== "string") return String(fallback);
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ─── Stat — single cell ───────────────────────────────────────────────────

export interface StatItem {
  /** Pre-resolved label. Provide this OR `labelKey`. */
  label?: ReactNode;
  /** i18n key resolved internally via `useTranslation`. When set, `label` is ignored for display (but still used as testid fallback if the translation is not a string). */
  labelKey?: string;
  value: ReactNode;
  /** Rendered in place of `value` when `value` is `null` or `undefined` and `loading` is false. Defaults to `"—"`. `value === 0` still renders `"0"`. */
  emptyText?: string;
  sub?: ReactNode;
  valueTone?: StatTone;
  subTone?: StatTone;
  progressPct?: number;
}

export interface StatInput {
  stat: StatItem;
  last?: boolean;
  loading?: boolean;
  testId?: string;
}

export type StatProps = StatInput;

export function Stat({
  stat,
  last = false,
  loading = false,
  testId,
}: StatProps) {
  const { t } = useTranslation();
  const valueTone = VALUE_TONE[stat.valueTone ?? "default"];
  const subTone   = SUB_TONE[stat.subTone   ?? "default"];

  const resolvedLabel: ReactNode = stat.labelKey ? t(stat.labelKey) : stat.label;
  const emptyText = stat.emptyText ?? "—";
  const resolvedValue: ReactNode =
    !loading && (stat.value === null || stat.value === undefined)
      ? emptyText
      : stat.value;

  return (
    <Box
      testId={testId}
      paddingX="xl" paddingY="xl"
      divider={last ? undefined : "right"}
    >
      <Stack gap="xs">
        <TextValue as="div" size="xs" weight="semibold" tone="muted" uppercase>
          {resolvedLabel}
        </TextValue>

        <TextValue as="div" size="xl" weight="bold" tabular tone={valueTone} lineHeight="snug">
          {loading
            ? <SkeletonBlock width={SKELETON.statW} height={SKELETON.statH} />
            : resolvedValue}
        </TextValue>

        {stat.progressPct !== undefined && !loading && (
          <ProgressBar percent={stat.progressPct} tone="revenue" size="sm" />
        )}

        {stat.sub != null && (
          <TextValue as="div" size="xs" weight="medium" tone={subTone} lineHeight="relaxed">
            {loading
              ? <SkeletonBlock width={SKELETON.statSubW} height={SKELETON.statSubH} />
              : stat.sub}
          </TextValue>
        )}
      </Stack>
    </Box>
  );
}

// ─── StatBand — composite ─────────────────────────────────────────────────

export interface StatBandInput {
  stats: StatItem[];
  loading?: boolean;
  testId?: string;
}

export type StatBandProps = StatBandInput;

export function StatBand({
  stats,
  loading = false,
  testId,
}: StatBandProps) {
  const { t } = useTranslation();
  return (
    <Box testId={testId} divider={["top", "bottom"]}>
      <Grid columns={stats.length} gap="none">
        {stats.map((stat, i) => {
          // For testid slugification, prefer the resolved label (translated string
          // when labelKey is set) so `stat-revenue-ytd` works even for i18n cells.
          const slugLabel = stat.labelKey ? t(stat.labelKey) : stat.label;
          return (
            <Stat
              key={i}
              stat={stat}
              last={i === stats.length - 1}
              loading={loading}
              testId={`stat-${slugify(slugLabel, i)}`}
            />
          );
        })}
      </Grid>
    </Box>
  );
}
